import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Map, GoogleApiWrapper, Marker, Circle } from "google-maps-react";

import "./GoogleMaps.scss";

const GoogleMaps = ({
  className,
  viewOnly,
  isMarker,
  getCoords,
  savedCoords,
  wrapperClasses,
  markerMoved,
  updatedPosition,
  showCircle,
  circleRadius,
}) => {
  // the user positin in ordinary map
  const [position, setPosition] = useState({
    coords: { lng: 0, lat: 0 },
  });

  // Parents build these objects on every render, so depend on the coordinates only;
  // otherwise each parent render resets `position` and the marker is rebuilt mid-drag.
  const updatedLat = updatedPosition?.coords?.lat;
  const updatedLng = updatedPosition?.coords?.lng;
  const savedLat = savedCoords?.coords?.lat;
  const savedLng = savedCoords?.coords?.lng;

  useEffect(() => {
    if (updatedLat && updatedLng) {
      setPosition({ coords: { lat: updatedLat, lng: updatedLng } });
    }
  }, [updatedLat, updatedLng]);

  useEffect(() => {
    if (isMarker && savedLat && savedLng) {
      setPosition({ coords: { lat: savedLat, lng: savedLng } });
    }
  }, [savedLat, savedLng, isMarker]);

  useEffect(() => {
    if (!savedLat && !savedLng) {
      navigator.geolocation.getCurrentPosition((userPosition) => {
        const {
          coords: { longitude, latitude },
        } = userPosition;
  
        setPosition({
          coords: {
            lng: longitude,
            lat: latitude,
          },
        });
      });
    }
  }, [savedLat, savedLng]);

  // Marker and Map compare these by reference; keep them stable between coordinate changes.
  const { lat: currentLat, lng: currentLng } = position.coords;
  const currentPoint = useMemo(
    () => ({ lat: currentLat, lng: currentLng }),
    [currentLat, currentLng],
  );

  const onMoveMarker = (props, marker) => {
    markerMoved();
    // DO NOT remove the props param even if it's not used
    setPosition({
      coords: {
        lng: marker.position.lng(),
        lat: marker.position.lat(),
      },
    });
    getCoords({
      coords: {
        lng: marker.position.lng(),
        lat: marker.position.lat(),
      },
    });
  };

  return (
    <div
      className={`google-maps-container position-relative ${wrapperClasses}`}>
      <Map
        google={window.google}
        zoom={14}
        center={currentPoint}
        className={className}>
        {isMarker && (
          <Marker
            name="Current location"
            draggable={!viewOnly}
            onDragend={onMoveMarker}
            position={currentPoint}
          />
        )}
        {showCircle && (
          <Circle
            radius={circleRadius}
            center={currentPoint}
            strokeColor="#448bca"
            strokeOpacity={1}
            strokeWeight={1}
            fillColor="#448bca"
            fillOpacity={0.3}
          />
        )}
      </Map>
    </div>
  );
};
export default GoogleApiWrapper({
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
})(GoogleMaps);

GoogleMaps.propTypes = {
  className: PropTypes.string,
  wrapperClasses: PropTypes.string,
  viewOnly: PropTypes.bool,
  isMarker: PropTypes.bool,
  getCoords: PropTypes.func,
  savedCoords: PropTypes.object,
  updatedPosition: PropTypes.object,
  hasSearch: PropTypes.bool,
  markerMoved: PropTypes.func,
  showCircle: PropTypes.bool,
  circleRadius: PropTypes.number,
};
GoogleMaps.defaultProps = {
  viewOnly: false,
};
